import React, { useState } from 'react';
import styled from 'styled-components';
import { Divider, Spin, Tabs } from 'antd';
import { SoundOutlined, PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';

// 基础消息样式
const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px 0;
`;

const MessageTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    color: #333;
    font-weight: 500;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
`;

const MessageContent = styled.div`
    font-size: 14px;
    color: #666;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MessageItem = styled.div`
    display: flex;
    align-items: flex-start;
    line-height: 1.6;
    padding: 4px 0;

    &:not(:last-child) {
        border-bottom: 1px dashed #eee;
        padding-bottom: 8px;
    }
`;

const MessageLabel = styled.span`
    font-weight: 500;
    color: #333;
    min-width: 60px;
    margin-right: 8px;
`;

const MessageText = styled.span`
    flex: 1;
    color: #666;
    white-space: pre-wrap;
`;

// 全屏详情样式
const DetailContainer = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: ${props => props.isFullscreen ? '800px' : '100%'};
    margin: ${props => props.isFullscreen ? '0 auto' : '0'};
    padding: ${props => props.isFullscreen ? '0' : '0'};
`;

const DetailTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #333;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
`;

const InfoList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const InfoItem = styled.div`
    border-bottom: 1px solid #eee;
    padding: 16px 0;
    width: 100%;

    &:last-child {
        border-bottom: none;
    }
`;

const InfoTitle = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const InfoTitleText = styled.h3`
    margin: 0;
    font-size: 16px;
    color: #333;
    font-weight: 500;
`;

const AudioButton = styled.button<{ isPlaying?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: ${props => props.isPlaying ? '#07C160' : '#666'};
    transition: color 0.3s ease;

    &:hover {
        color: #07C160;
    }
`;

const InfoDescription = styled.p`
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    white-space: pre-wrap;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
`;

// 添加视频相关样式
const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    margin-bottom: 16px;
`;

const VideoThumbnail = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 8px;
`;

const PlayButton = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    font-size: 48px;
    opacity: 0.8;
    transition: opacity 0.3s;

    &:hover {
        opacity: 1;
    }
`;

const VideoTitle = styled.div`
    margin-top: 8px;
    font-size: 14px;
    color: #333;
`;

// 添加图片相关样式
const ImageContainer = styled.div`
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
`;

const StyledImage = styled.img`
    width: 100%;
    max-height: 400px;
    object-fit: contain;
    border-radius: 8px;
`;

// 接口定义
interface AudioInfo {
    duration: number;
    url: string;
}

interface Group {
    id: string;
    name: string;
    items: Item[];
}

interface Item {
    name: string;
    description?: string;
    audio?: AudioInfo;
    type: 'video' | 'str' | 'picture';
    path?: string;
    thumbnailPath?: string;
    max_version_code: number;
    app_router?: {
        arguments: {
            cover: string;
            id: number;
            initIndex: number;
            name: string;
            type: string;
        };
        label: string;
        path: string;
        type: string;
    };
}

interface MedicineInfo {
    title: string;
    description: string;
    audio?: AudioInfo;
    type: string;
}

interface KnowledgeItemSummaryProps {
    name: string;
    medicineInfoList: MedicineInfo[];
    isFullscreen: boolean,
    contentType: string,
    contentID: string
}

// 视频项组件
const VideoItem: React.FC<{ item: Item }> = ({ item }) => {
    // 控制视频播放状态
    const [isPlaying, setIsPlaying] = useState(false);
    
    const handleVideoClick = () => {
        setIsPlaying(!isPlaying);
    };

    // 获取视频和缩略图URL
    const videoUrl = `https://api.lighttcm.com/api/resource/${item.path}`;
    const thumbnailUrl = item.thumbnailPath
        ? `https://api.lighttcm.com/api/resource/${item.thumbnailPath}`
        : item.app_router?.arguments.cover;

    return (
        <InfoItem>
            <InfoTitle>
                <InfoTitleText>{item.name}</InfoTitleText>
            </InfoTitle>
            <VideoContainer onClick={handleVideoClick}>
                {isPlaying ? (
                    // 播放状态显示视频
                    <video 
                        src={videoUrl}
                        controls
                        autoPlay
                        style={{ width: '100%', height: '100%' }}
                        poster={thumbnailUrl}
                    />
                ) : (
                    // 未播放状态显示缩略图和播放按钮
                    <>
                        <VideoThumbnail src={thumbnailUrl} alt={item.name} />
                        <PlayButton>
                            <PlayCircleOutlined />
                        </PlayButton>
                    </>
                )}
            </VideoContainer>
        </InfoItem>
    );
};

// 文本项组件
const TextItem: React.FC<{
    item: Item;
    index: number;
    currentPlayingIndex: number | null;
    onAudioPlay: (index: number, audioUrl?: string) => void;
}> = ({ item, index, currentPlayingIndex, onAudioPlay }) => {
    return (
        <InfoItem>
            <InfoTitle>
                <InfoTitleText>{item.name}</InfoTitleText>
                {item.audio && (
                    <AudioButton
                        onClick={() => onAudioPlay(index, item.audio?.url)}
                        isPlaying={currentPlayingIndex === index}
                    >
                        {currentPlayingIndex === index ? (
                            <PauseOutlined style={{ fontSize: '20px' }} />
                        ) : (
                            <SoundOutlined style={{ fontSize: '20px' }} />
                        )}
                    </AudioButton>
                )}
            </InfoTitle>
            <InfoDescription>{item.description}</InfoDescription>
        </InfoItem>
    );
};

// 添加图片组件
const PictureItem: React.FC<{ item: Item }> = ({ item }) => {
    const imageUrl = `https://api.lighttcm.com/api/resource/${item.path}`;
    
    return (
        <InfoItem>
            <InfoTitle>
                <InfoTitleText>{item.name}</InfoTitleText>
            </InfoTitle>
            <ImageContainer>
                <StyledImage src={imageUrl} alt={item.name} />
            </ImageContainer>
        </InfoItem>
    );
};

// 组件实现
const KnowledgeItemSummary: React.FC<KnowledgeItemSummaryProps> = ({
    name,
    medicineInfoList,
    isFullscreen,
    contentType,
    contentID
}) => {
    const [loading, setLoading] = useState(false);
    const [currentPlayingIndex, setCurrentPlayingIndex] = useState<number | null>(null);
    const [audioElements] = useState<{ [key: number]: HTMLAudioElement }>({});
    const [fullContent, setFullContent] = useState<Group[]>([]);

    // 获取详细内容的接口请求
    const fetchFullContent = async () => {
        try {
            // 发起请求获取详细内容
            const response = await fetch(
                `https://api.lighttcm.com/api/std/app/content/${contentType}/${contentID}?version-code=78`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('获取详细内容失败');
            }

            const data = await response.json();

            // 更新详细内容状态
            setFullContent(data.data.groups);
            setLoading(false);
        } catch (error) {
            console.error('获取详细内容出错:', error);
            setLoading(false);
        }
    };

    // 当全屏模式改变时获取详细内容
    React.useEffect(() => {
        if (isFullscreen && contentType && contentID) {
            fetchFullContent();
        }
    }, [isFullscreen, contentType, contentID]);


    React.useEffect(() => {
        if (isFullscreen) {
            setLoading(true);

        }
    }, [isFullscreen]);

    const handleAudioPlay = (index: number, audioUrl?: string) => {
        if (!audioUrl) return;

        if (currentPlayingIndex !== null && currentPlayingIndex !== index) {
            audioElements[currentPlayingIndex]?.pause();
        }

        if (!audioElements[index]) {
            const audio = new Audio(audioUrl);
            audio.addEventListener('ended', () => {
                setCurrentPlayingIndex(null);
            });
            audioElements[index] = audio;
        }

        if (currentPlayingIndex === index) {
            audioElements[index].pause();
            setCurrentPlayingIndex(null);
        } else {
            audioElements[index].play();
            setCurrentPlayingIndex(index);
        }
    };

    // 渲染消息模式
    if (!isFullscreen) {
        return (
            <MessageContainer>
                <MessageTitle>{name}</MessageTitle>
                <MessageContent>
                    {medicineInfoList.map((info, index) => (
                        <MessageItem key={index}>
                            <MessageLabel>{info.title}</MessageLabel>
                            <MessageText>{info.description}</MessageText>
                        </MessageItem>
                    ))}
                </MessageContent>
            </MessageContainer>
        );
    }

    // 渲染加载状态
    if (loading) {
        return (
            <LoadingContainer>
                <Spin size="large" />
            </LoadingContainer>
        );
    }

    // 渲染全屏详情模式
    return (
        <DetailContainer isFullscreen={isFullscreen}>
            <DetailTitle>{name}</DetailTitle>
            <InfoList>
                <Tabs key={`${contentID}-${contentType}`}>
                    {fullContent.map((group, groupIndex) => (
                        <Tabs.TabPane tab={group.name} key={group.id}>
                            {group.items.map((item, index) => {
                                switch (item.type) {
                                    case 'video':
                                        return <VideoItem key={index} item={item} />;
                                    case 'picture':
                                        return <PictureItem key={index} item={item} />;
                                    default:
                                        return (
                                            <TextItem
                                                key={index}
                                                item={item}
                                                index={index}
                                                currentPlayingIndex={currentPlayingIndex}
                                                onAudioPlay={handleAudioPlay}
                                            />
                                        );
                                }
                            })}
                        </Tabs.TabPane>
                    ))}
                </Tabs>
            </InfoList>
        </DetailContainer>
    );
};

export default KnowledgeItemSummary;
